import { useEffect, useRef } from "react";
import lottie from "lottie-web";
import diamondData from "./lottie/diamond.json";
import rewardBoxData from "./lottie/reward-box.json";
import coinAnimatedData from "./lottie/coin-animated.json";
import coinImg from "./img/coin_img.png";
import giftImg from "./img/icon_gift.png";
import gplayImg from "./img/gplay_icon.png";

// Animations extracted from the apps' .lottie bundles: diamond and reward-box
// from mobile/playmax/assets/animations, coin-animated from
// mobile/moneycash_android/assets/animations (used only by Burning Rewards).
// Diamond and coin-animated play at 0.4x in the apps.

const MLottie: React.FC<{
  data: any;
  size?: number;
  speed?: number;
}> = ({ data, size = 18, speed = 0.4 }) => {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!ref.current) return;
    const animation = lottie.loadAnimation({
      container: ref.current,
      renderer: "svg",
      loop: true,
      autoplay: true,
      animationData: data,
    });
    animation.setSpeed(speed);
    return () => animation.destroy();
  }, [data, speed]);

  return (
    <div
      ref={ref}
      style={{ width: size, height: size, flexShrink: 0, display: "inline-block" }}
    />
  );
};

// The app's CoinLottieView is always called with `isImage` on the rework screens,
// which renders assets/images/coin_img.png statically instead of animating.
export const CoinIcon: React.FC<{ size?: number }> = ({ size = 18 }) => (
  <img
    src={coinImg}
    alt=""
    width={size}
    height={size}
    style={{ flexShrink: 0, display: "inline-block" }}
  />
);

export const GiftIcon: React.FC<{ size?: number }> = ({ size = 40 }) => (
  <img
    src={giftImg}
    alt=""
    width={size}
    height={size}
    style={{ flexShrink: 0, display: "inline-block", objectFit: "contain" }}
  />
);

// the badge on the offer card's CTA icon (playtime_android assets/gplay_icon.png)
// size may be a CSS length (the offer card scales it with its container)
export const PlayStoreIcon: React.FC<{ size?: number | string }> = ({
  size = 12,
}) => (
  <img
    src={gplayImg}
    alt=""
    style={{
      width: size,
      height: size,
      flexShrink: 0,
      display: "inline-block",
      objectFit: "contain",
    }}
  />
);

// Burning Rewards is the one card that animates its coins
// (moneycash_android BurningRewards/View.tsx :158-163, coin-animated at 0.4x)
export const CoinAnimatedIcon: React.FC<{ size?: number }> = ({ size = 26 }) => (
  <MLottie data={coinAnimatedData} size={size} />
);

// Diamond/index.tsx bakes in a -deviceHeight/240 vertical nudge
export const DiamondIcon: React.FC<{ size?: number }> = ({ size = 18 }) => (
  <div style={{ position: "relative", top: -3.55, display: "inline-block" }}>
    <MLottie data={diamondData} size={size} />
  </div>
);

export const RewardBoxIcon: React.FC<{ size?: number }> = ({ size = 35.5 }) => (
  <MLottie data={rewardBoxData} size={size} speed={1} />
);

export default MLottie;
